import React, { useState, useEffect } from 'react';
import { Percent, Plus, Trash2, Save } from 'lucide-react';
import { usePricing, VolumeDiscount } from '@context/PricingContext';
import Button from '@components/common/Button';
import Input from '@components/common/Input';
import Alert from '@components/common/Alert';

interface DiscountTiersEditorProps {
  onDataChange?: () => void;
  onSaveSuccess?: () => void;
}

const DiscountTiersEditor: React.FC<DiscountTiersEditorProps> = ({
  onDataChange,
  onSaveSuccess,
}) => {
  const { configuration, loading, updateVolumeDiscounts } = usePricing();
  const [discounts, setDiscounts] = useState<VolumeDiscount[]>([
    { photoCount: 5, discountPercent: 10 },
    { photoCount: 10, discountPercent: 15 },
    { photoCount: 20, discountPercent: 20 },
    { photoCount: 50, discountPercent: 25 },
  ]);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    if (configuration?.volumeDiscounts) {
      setDiscounts([...configuration.volumeDiscounts].sort((a, b) => a.photoCount - b.photoCount));
    }
  }, [configuration?.volumeDiscounts]);

  const handleDiscountChange = (index: number, field: keyof VolumeDiscount, value: string) => {
    const numericValue = Math.max(0, parseInt(value) || 0);
    const maxValue = field === 'discountPercent' ? 99 : 999;
    const clampedValue = Math.min(numericValue, maxValue);

    const updatedDiscounts = discounts.map((discount, i) =>
      i === index ? { ...discount, [field]: clampedValue } : discount
    );

    // Sort by photo count to maintain order
    updatedDiscounts.sort((a, b) => a.photoCount - b.photoCount);
    setDiscounts(updatedDiscounts);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const addDiscount = () => {
    const newPhotoCount =
      discounts.length > 0 ? Math.max(...discounts.map(d => d.photoCount)) + 10 : 5;

    const newDiscount: VolumeDiscount = {
      photoCount: newPhotoCount,
      discountPercent: 10,
    };

    const updatedDiscounts = [...discounts, newDiscount].sort(
      (a, b) => a.photoCount - b.photoCount
    );
    setDiscounts(updatedDiscounts);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const removeDiscount = (index: number) => {
    const updatedDiscounts = discounts.filter((_, i) => i !== index);
    setDiscounts(updatedDiscounts);
    setSaveSuccess(false);
    onDataChange?.();
  };

  const handleSave = async () => {
    setSaveLoading(true);
    setSaveError(null);
    setSaveSuccess(false);

    // Validate discounts
    const errors = [];
    const photoCounts = discounts.map(d => d.photoCount);
    const uniquePhotoCounts = new Set(photoCounts);

    if (photoCounts.length !== uniquePhotoCounts.size) {
      errors.push('Photo counts must be unique');
    }

    if (discounts.some(d => d.photoCount < 1)) {
      errors.push('Photo counts must be at least 1');
    }

    if (discounts.some(d => d.discountPercent < 0 || d.discountPercent > 99)) {
      errors.push('Discount percentages must be between 0% and 99%');
    }

    if (errors.length > 0) {
      setSaveError(errors.join('. '));
      setSaveLoading(false);
      return;
    }

    try {
      await updateVolumeDiscounts(discounts);
      setSaveSuccess(true);
      onSaveSuccess?.();
    } catch (err: any) {
      setSaveError(err.message || 'Failed to save discounts');
    } finally {
      setSaveLoading(false);
    }
  };

  const calculateDiscountValue = (discount: VolumeDiscount): string => {
    if (!configuration?.basePrices) return '$0.00';

    const basePrice = configuration.basePrices.singlePhoto * discount.photoCount;
    const discountAmount = basePrice * (discount.discountPercent / 100);
    return `$${(discountAmount / 100).toFixed(2)}`;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <Percent className="h-5 w-5 text-green-600 mr-2" />
            <h2 className="text-lg font-semibold text-gray-900">Volume Discounts</h2>
          </div>
          <Button
            variant="ghost"
            size="small"
            onClick={addDiscount}
            icon={<Plus className="h-4 w-4" />}
          >
            Add Tier
          </Button>
        </div>
        <p className="text-sm text-gray-600 mt-1">
          Offer discounts for customers buying multiple photos.
        </p>
      </div>

      <div className="p-6 space-y-6">
        {saveError && <Alert type="error" title="Save Failed" message={saveError} />}

        {saveSuccess && (
          <Alert
            type="success"
            title="Saved Successfully"
            message="Your volume discounts have been updated."
          />
        )}

        {discounts.length === 0 ? (
          <div className="text-center py-8">
            <Percent className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No volume discounts configured</p>
            <Button
              variant="outline"
              onClick={addDiscount}
              className="mt-4"
              icon={<Plus className="h-4 w-4" />}
            >
              Add First Discount Tier
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {discounts.map((discount, index) => (
              <div
                key={`${discount.photoCount}-${index}`}
                className="border border-gray-200 rounded-lg p-4 bg-gray-50"
              >
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                  <div>
                    <Input
                      label="Photo Count"
                      type="number"
                      value={discount.photoCount.toString()}
                      onChange={e => handleDiscountChange(index, 'photoCount', e.target.value)}
                      placeholder="10"
                      min="1"
                      max="999"
                    />
                  </div>

                  <div>
                    <Input
                      label="Discount %"
                      type="number"
                      value={discount.discountPercent.toString()}
                      onChange={e => handleDiscountChange(index, 'discountPercent', e.target.value)}
                      placeholder="15"
                      min="0"
                      max="99"
                      rightIcon={<Percent className="h-4 w-4" />}
                    />
                  </div>

                  <div className="text-sm text-gray-600">
                    <div className="font-medium">Discount Value</div>
                    <div className="text-green-600 font-semibold">
                      {calculateDiscountValue(discount)}
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="small"
                      onClick={() => removeDiscount(index)}
                      icon={<Trash2 className="h-4 w-4" />}
                      className="text-red-600 hover:bg-red-50"
                    >
                      Remove
                    </Button>
                  </div>
                </div>

                {/* Tier Description */}
                <div className="mt-3 text-sm text-gray-600 bg-white rounded p-3">
                  <strong>Customer sees:</strong> "Buy {discount.photoCount}+ photos and save{' '}
                  {discount.discountPercent}% (Save {calculateDiscountValue(discount)} on{' '}
                  {discount.photoCount} photos)"
                </div>
              </div>
            ))}
          </div>
        )}

        {discounts.length > 0 && (
          <>
            {/* Discount Preview */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">How It Works</h3>
              <div className="text-sm text-blue-700 space-y-1">
                <div>
                  • Discounts apply automatically when customers reach photo count thresholds
                </div>
                <div>
                  • Higher discounts override lower ones (customer gets best available discount)
                </div>
                <div>• Discounts apply to the total order, not per photo</div>
                <div>• Bundle prices are compared against discounted individual prices</div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSave}
                loading={saveLoading}
                disabled={loading}
                icon={<Save className="h-4 w-4" />}
              >
                Save Discounts
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DiscountTiersEditor;
